/**
 * @module Account
 * Public account and transfer primitives used by chain-specific clients.
 */

import { IKeybanApi } from "~/api";
import type { Address, Hash, Hex } from "~/index";

import { EvmFeeDetails } from "./evm/account";
import { StellarFeeDetails } from "./stellar/account";

export type FeeDetails = EvmFeeDetails | StellarFeeDetails;
/**
 * Represents a transaction fee estimation returned by estimation methods.
 * The concrete shape of the `details` field depends on the underlying network:
 * - EVM chains: {@link EvmFeeDetails} (EIP-1559 fields)
 * - Stellar: {@link StellarFeeDetails} (fee per operation as string)
 * @see {@link KeybanAccount.estimateTransfer}
 * @see {@link KeybanAccount.estimateERC20Transfer}
 * @see {@link KeybanAccount.estimateNftTransfer}
 */
export type FeesEstimation = {
  /**
   * The total maximum fees for the transaction.
   */
  maxFees: bigint;

  /**
   * Parameters to set when constructing a transaction to ensure the estimation maxFees.
   */
  details: FeeDetails;
};

/**
 * Parameters for transferring ERC‑20-like fungible tokens.
 * @template TFeeDetails Network-specific fee type (defaults to {@link FeeDetails}).
 * @property contractAddress The token contract address.
 * @property to The recipient wallet address.
 * @property value Amount to transfer, in the token's smallest unit.
 * @property [fees] Optional network-specific fee parameters.
 * @see {@link KeybanAccount.transferERC20}
 * @see {@link SdkErrorTypes}
 */
export type TransferERC20Params<TFeeDetails = FeeDetails> = {
  contractAddress: Address;
  to: Address;
  value: bigint;
  fees?: TFeeDetails;
};

/**
 * Parameters for transferring NFTs (ERC‑721 / ERC‑1155 on EVM chains).
 * @template TFeeDetails Network-specific fee type (defaults to {@link FeeDetails}).
 * @property contractAddress The NFT contract address.
 * @property tokenId The token identifier.
 * @property to The recipient wallet address.
 * @property [value] Amount to transfer for ERC‑1155 (required and > 0), ignored for ERC‑721.
 * @property standard NFT standard to use: "ERC721" | "ERC1155".
 * @property [fees] Optional network-specific fee parameters.
 * @see {@link KeybanAccount.transferNft}
 * @see {@link SdkErrorTypes}
 */
export type TransferNftParams<TFeeDetails = FeeDetails> = {
  contractAddress: Address;
  tokenId: bigint;
  to: Address;
  value?: bigint;
  standard: "ERC721" | "ERC1155";
  fees?: TFeeDetails;
};

/**
 * Parameters to estimate an ERC‑20 transfer, without fees.
 * Derived from {@link TransferERC20Params} by omitting `fees`.
 */
export type EstimateERC20TransferParams = Omit<TransferERC20Params, "fees">;
/**
 * Parameters to estimate an NFT transfer, without fees.
 * Derived from {@link TransferNftParams} by omitting `fees`.
 */
export type EstimateNftTransferParams = Omit<TransferNftParams, "fees">;

/**
 * Abstract account bound to a specific chain implementation (EVM, Starknet, Stellar).
 * Provides methods to sign messages and to transfer/estimate fees for native currency,
 * ERC‑20-like tokens, and NFTs, when supported by the target network.
 * Obtain an instance via {@link KeybanClient.initialize}.
 * @see {@link KeybanClient}
 */
export abstract class KeybanAccount {
  /**
   * The blockchain address associated with the account.
   */
  abstract address: Address;

  /**
   * The public key associated with the account.
   */
  abstract publicKey: string;

  protected api: IKeybanApi;

  /**
   * Internal constructor used by chain-specific clients.
   * @param api - Keyban API facade for remote operations.
   * @internal
   */
  constructor(api: IKeybanApi) {
    this.api = api;
  }

  /**
   * Sign a message with the account's key material.
   * @param message - The message to sign.
   * @returns Promise resolving to the signature. EVM returns hex, Stellar returns base64 string.
   * @throws {Error} On signing failures.
   * @see {@link KeybanClient}
   * @example
   * import { KeybanClient, KeybanNetwork } from "@keyban/sdk-base";
   *
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   * const account = await client.initialize();
   * const signature = await account.signMessage("Hello Keyban");
   * console.log(signature);
   */
  abstract signMessage(message: string): Promise<Hex | string | string[]>;

  /**
   * Transfer native currency to a recipient.
   * @param to - Recipient address.
   * @param value - Amount to transfer in the smallest unit (e.g., wei, stroop).
   * @param [fees] - Optional network-specific fee parameters.
   * @returns Promise resolving to the transaction hash/string.
   * @throws {SdkError} {@link SdkErrorTypes.AddressInvalid} | {@link SdkErrorTypes.AmountInvalid}
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   *
   * const client = new KeybanClient({
   *   apiUrl: "https://api.prod.keyban.io",
   *   appId: "YOUR_APP_ID",
   *   network: KeybanNetwork.EthereumAnvil,
   * });
   * const account = await client.initialize();
   *
   * const recipient: Address = "0x0000000000000000000000000000000000000001";
   * const value = 1_000_000_000_000_000_000n; // 1 ETH in wei
   * const txHash = await account.transfer(recipient, value);
   * console.log(txHash);
   */
  abstract transfer(
    to: Address,
    value: bigint,
    fees?: FeeDetails,
  ): Promise<Hash | string>;

  /**
   * Estimate maximum fees to transfer native currency.
   * @param to - Recipient address.
   * @returns Promise resolving to {@link FeesEstimation}.
   * @throws {Error} On estimation failure.
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   * const account = await client.initialize();
   * const recipient: Address = "0x0000000000000000000000000000000000000001";
   * const estimation = await account.estimateTransfer(recipient);
   * console.log(estimation.maxFees.toString());
   */
  abstract estimateTransfer(to: Address): Promise<FeesEstimation>;

  /**
   * Transfer ERC‑20-like fungible tokens.
   * @param params - Transfer parameters.
   * @returns Promise resolving to the transaction hash/string.
   * @throws {SdkError} {@link SdkErrorTypes.AddressInvalid} | {@link SdkErrorTypes.AmountInvalid} | {@link SdkErrorTypes.RecipientAddressEqualsSender}
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   *
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   *
   * const txHash = await account.transferERC20({
   *   contractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" as Address, // USDC
   *   to: "0x0000000000000000000000000000000000000001" as Address,
   *   value: 1_000_000n, // 1 USDC (6 decimals)
   * });
   * console.log(txHash);
   */
  abstract transferERC20(params: TransferERC20Params): Promise<Hash | string>;

  /**
   * Estimate maximum fees for an ERC‑20 token transfer.
   * @param params - Transfer parameters without explicit fees.
   * @returns Promise resolving to {@link FeesEstimation}.
   * @throws {Error} On estimation failure.
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   * const account = await client.initialize();
   * const estimation = await account.estimateERC20Transfer({
   *   contractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" as Address,
   *   to: "0x0000000000000000000000000000000000000001" as Address,
   *   value: 1_000_000n,
   * });
   * console.log(estimation.details);
   */
  abstract estimateERC20Transfer(
    params: EstimateERC20TransferParams,
  ): Promise<FeesEstimation>;

  /**
   * Transfer an NFT (ERC‑721 / ERC‑1155).
   * @param params - NFT transfer parameters.
   * @returns Promise resolving to the transaction hash.
   * @throws {SdkError} {@link SdkErrorTypes.AddressInvalid} | {@link SdkErrorTypes.RecipientAddressEqualsSender} | {@link SdkErrorTypes.AmountRequired} | {@link SdkErrorTypes.AmountInvalid} | {@link SdkErrorTypes.AmountIrrelevant} | {@link SdkErrorTypes.InvalidNftStandard}
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   *
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   * const account = await client.initialize();
   *
   * // ERC-721
   * await account.transferNft({
   *   contractAddress: "0xABC..." as Address,
   *   tokenId: 123n,
   *   to: "0x0000000000000000000000000000000000000001" as Address,
   *   standard: "ERC721",
   * });
   *
   * // ERC-1155
   * await account.transferNft({
   *   contractAddress: "0xDEF..." as Address,
   *   tokenId: 456n,
   *   to: "0x0000000000000000000000000000000000000001" as Address,
   *   value: 5n,
   *   standard: "ERC1155",
   * });
   */
  abstract transferNft(params: TransferNftParams): Promise<Hash>;

  /**
   * Estimate maximum fees for an NFT transfer (ERC‑721 / ERC‑1155).
   * @param params - NFT estimation parameters (without explicit fees).
   * @returns Promise resolving to {@link FeesEstimation}.
   * @throws {Error} On estimation failure or invalid parameters.
   * @example
   * import { KeybanClient, KeybanNetwork, type Address } from "@keyban/sdk-base";
   * const client = new KeybanClient({ apiUrl: "https://api.prod.keyban.io", appId: "YOUR_APP_ID", network: KeybanNetwork.EthereumAnvil });
   * const account = await client.initialize();
   * const estimation = await account.estimateNftTransfer({
   *   contractAddress: "0xDEF..." as Address,
   *   tokenId: 456n,
   *   to: "0x0000000000000000000000000000000000000001" as Address,
   *   value: 5n,
   *   standard: "ERC1155",
   * });
   * console.log(estimation.maxFees);
   */
  abstract estimateNftTransfer(
    params: EstimateNftTransferParams,
  ): Promise<FeesEstimation>;
}
